import i18next, { t } from "i18next";
import { capitalize } from "neetocist";
import { identity, last, pluck } from "ramda";
import * as yup from "yup";

import {
  NEETO_WIDGET_USER_IDENTITY_KEY,
  SESSION_CONTEXT_KEY,
  RAILS_ENV,
  NEETO_WIDGET_SCRIPT_URL,
  SAMPLE_USER_IDENTITY_CODE_STRING,
  WIDGET_DESCRIPTIONS,
} from "./constants";

const buildEmbedWidgetScript = () => {
  const widgetScriptUrl =
    window.globalProps.railsEnv === RAILS_ENV.production
      ? NEETO_WIDGET_SCRIPT_URL.production
      : NEETO_WIDGET_SCRIPT_URL.staging;

  return `<script src="${widgetScriptUrl}"></script>`;
};

const indentAndFormat = (str, formatter = identity) =>
  str.split("\n").map(formatter).join("\n  ");

const getCommentedCommand = (command, isEnabled) =>
  isEnabled ? command : `/// ${command}`;

const embedWidgetEnvironment = () => {
  switch (window.globalProps.railsEnv) {
    case RAILS_ENV.production:
      return "";
    case RAILS_ENV.development:
      return `environment: "${RAILS_ENV.development}",`;
    case RAILS_ENV.staging:
      return `environment: "${RAILS_ENV.staging}",`;
    default:
      return "/* Unidentified RAILS_ENV, */";
  }
};

const embedWidgetApiKey = apiKey => `apiKey: "${apiKey}",`;

const addEngageOptions = () => `neetoEngageOptions: {
  elementId: "neetoengage-trigger",
},`;

const wrapBlockInOnLoad = codeBlock =>
  `window.addEventListener("load", () => ${codeBlock});`;

const embedWidgetFunctionCall = (enabledWidgets, apiKey) => `embedNeetoWidget({
  ${embedWidgetEnvironment()}
  ${indentAndFormat(embedWidgetApiKey(apiKey))}
  ${indentAndFormat(addEngageOptions(), cmd =>
    getCommentedCommand(cmd, enabledWidgets.engage)
  )}
})`;

const filterMarkedCommands = queryString =>
  queryString
    .split("\n")
    .filter(line => !line.includes("///") && line.trim())
    .join("\n");

export const getSampleUserIdentity = (enabledWidgets, selectedFramework) => {
  const splitCodeLines =
    SAMPLE_USER_IDENTITY_CODE_STRING[selectedFramework].split("\n");

  return filterMarkedCommands(
    splitCodeLines
      .map(cmd => {
        const isEmailKey = cmd.includes("email");
        const isObjectStartLine = cmd === splitCodeLines[0];
        const isObjectEndLine = cmd === last(splitCodeLines);
        const isLineHidden =
          !isEmailKey && !isObjectStartLine && !isObjectEndLine;
        if (
          (!enabledWidgets.chat && isLineHidden) ||
          (!enabledWidgets.chat && !enabledWidgets.replay)
        ) {
          return null;
        }

        return cmd;
      })
      .join("\n")
  );
};

export const sampleConsolidatedCodeString = (enabledWidgets, apiKey) =>
  filterMarkedCommands(`${buildEmbedWidgetScript()}
<script>
  ${
    enabledWidgets.chat || enabledWidgets.replay
      ? `window.${NEETO_WIDGET_USER_IDENTITY_KEY} = {};`
      : ""
  }
  ${enabledWidgets.replay ? `window.${SESSION_CONTEXT_KEY} = {};` : ""}

  ${indentAndFormat(
    wrapBlockInOnLoad(embedWidgetFunctionCall(enabledWidgets, apiKey))
  )}
</script>`);

export const getEmailWidgetSnippetFormInitialValues = subject => ({
  emails: "",
  subject,
});

export const getSortedPrefixedWidgets = widgets => {
  const sortedWidgets = widgets
    .sort()
    .map(widget => `Neeto${capitalize(widget)}`);

  return sortedWidgets.length === 1
    ? sortedWidgets
    : t("neetoMolecules.widget.email.widgetList", {
        widgets: sortedWidgets,
      });
};

export const getWidgetDescriptions = widgets => {
  const values = Object.entries(WIDGET_DESCRIPTIONS).filter(([widget]) =>
    widgets.includes(widget)
  );

  return pluck(1, values);
};

export const getEmailWidgetSnippetValidationSchema = () =>
  yup.object({
    emails: yup
      .array()
      .required()
      .label(i18next.t("neetoMolecules.widget.email.fields.emails.label")),
    subject: yup
      .string()
      .required()
      .label(i18next.t("neetoMolecules.widget.email.fields.subject.label")),
  });

export const getWidgetApiKeyUrl = () =>
  `api/widget/v1/api_keys/${window.globalProps.organization.subdomain}`;

export const shouldShowSwitches = () => false;
